import React, { useState, useEffect, useRef } from "react";
import { TextField, IconButton, Switch, Box, Paper, Button, Chip, Tooltip } from "@mui/material";
import { Search, RefreshCcw, X, Filter } from "lucide-react";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import { useGlobalFilters } from "../../context/GlobalFilterProvider";
import useFetchSiteId from "../../hooks/useFetchSiteId";
import AdvancedFilters from "./AdvancedFilters";
import { DEFAULT_TIME_RANGE, RANGE_TYPE_LABELS } from "../../utils/constants";
import { getRange } from "../../utils/utility";
import { getLocalStorage, LOCAL_STORAGE_KEYS, patchLocalStorage } from "../../utils/localStorageUtil";
import PropTypes from "prop-types";

const FilterBar = ({
  isLiveStatusShown = false,
  isRefreshShown = false,
  isAutoRefreshShown = false,
  onSearch = null,
  onDateChange = () => {},
  onMessageTypeChange = null,
  onFeatureChange = null,
  onDetailSearch = null,
  filters = null,
  isAdvancedFiltersShown = false,
  handleRefresh = null,
  isDatePickerRequired = true,
  isSerialNumberRequired = true,
}) => {
  const customDatePickerRef = useRef(null);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [filterType, setFilterType] = useState(DEFAULT_TIME_RANGE);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeFilterChips, setActiveFilterChips] = useState([]);
  const [hasLoadedFromStorage, setHasLoadedFromStorage] = useState(false);

  const { serialNumber, setState } = useGlobalFilters();
  const [searchText, setSearchText] = useState(isSerialNumberRequired ? serialNumber : "");
  const [isConfirmed, setIsConfirmed] = useState(serialNumber ? true : false);

  const { siteId } = useFetchSiteId(isConfirmed ? searchText : null, 0);

  useEffect(() => {
    if (!isConfirmed || !siteId) return;
    setState({ serialNumber: searchText, siteId });
  }, [siteId, isConfirmed, searchText, setState]);

  useEffect(() => {
    setSearchText(serialNumber);
  }, [serialNumber]);

  useEffect(() => {
    const chips = [];

    if (filters?.msgType) {
      chips.push({
        id: "msgType",
        label: `Type: ${filters.msgType}`,
        type: "msgType",
      });
    }

    if (Array.isArray(filters?.feature)) {
      filters.feature.forEach((f, idx) => {
        chips.push({
          id: `feature-${idx}`,
          label: `Feature: ${f}`,
          type: "feature",
          value: f,
        });
      });
    }

    if (filters?.search) {
      chips.push({
        id: "search",
        label: `Text: ${filters.search}`,
        type: "search",
      });
    }

    setActiveFilterChips(chips);
  }, [filters]);

  const handleRemoveChip = chip => {
    if (chip.type === "msgType") onMessageTypeChange?.("");
    if (chip.type === "feature") {
      onFeatureChange?.(filters.feature.filter(f => f !== chip.value));
    }
    if (chip.type === "search") onDetailSearch?.("");
  };

  const handleConfirmClick = () => {
    setIsConfirmed(true);
    onSearch?.(searchText);
    setState({ serialNumber: searchText, isSerialNumberConfirmed: true });
  };

  // Load persisted FilterBar state once on mount
  useEffect(() => {
    try {
      const localStorageFilterState = getLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE) || {};
      console.log("Local Storage Filter State->", localStorageFilterState);
    } catch (err) {
      // ignore and continue
    } finally {
      setHasLoadedFromStorage(true);
    }
  }, []);

  // Persist relevant FilterBar state whenever it changes
  useEffect(() => {
    // Avoid writing before initial load
    if (hasLoadedFromStorage) {
      try {
        patchLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE, {
          isConfirmed,
        });
        if (isConfirmed) {
          patchLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE, {
            serialNumber: searchText,
          });
        }
      } catch (err) {
        // swallow storage errors
      }
    }
  }, [searchText, isConfirmed]);

  const handleDateChange = (range, type) => {
    setState({ dateRange: range });
    if (type) setFilterType(type);
    onDateChange(range);
  };

  const handleRefreshClick = () => {
    if (customDatePickerRef.current?.handleCustomChange) {
      customDatePickerRef.current.handleCustomChange({ target: { value: filterType } }, true);
      const newRange = getRange(filterType);
      setState({ dateRange: newRange });
      onDateChange(newRange);
    }
    handleRefresh?.();
  };

  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: 2,
        backgroundColor: "#fff",
      }}
    >
      {/* -------- Top Row -------- */}
      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          size="small"
          placeholder="Search by Charger ID..."
          value={isConfirmed ? "" : searchText}
          onChange={e => {
            setSearchText(e.target.value);
            setIsConfirmed(false);
            setState({ isSerialNumberConfirmed: false });
          }}
          onKeyDown={e => {
            if (e.key === "Enter" && searchText) {
              handleConfirmClick();
            }
          }}
          InputProps={{
            endAdornment: (
              <Tooltip title="Click to search" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={handleConfirmClick}
                  disabled={!searchText}
                  sx={{
                    padding: "4px",
                    marginRight: "4px",
                    cursor: searchText ? "pointer" : "default",
                    opacity: searchText ? 1 : 0.5,
                    "&:hover": {
                      backgroundColor: searchText ? "rgba(0, 0, 0, 0.04)" : "transparent",
                    },
                  }}
                >
                  <Search size={18} />
                </IconButton>
              </Tooltip>
            ),
          }}
          sx={{ minWidth: 260 }}
        />

        {/* Inline Filter Icon */}
        {isAdvancedFiltersShown && (
          <Tooltip
            title={showAdvancedFilters ? "Hide advanced filters" : "Show advanced filters"}
            arrow
            placement="top"
          >
            <IconButton
              size="small"
              onClick={() => setShowAdvancedFilters(prev => !prev)}
              sx={{
                border: "1px solid var(--color-gray-border)",
                borderRadius: 1,
                p: "9px",
              }}
            >
              <Filter size={16} />
            </IconButton>
          </Tooltip>
        )}

        <Box display="flex" alignItems="center" gap={1} ml="auto">
          {isAutoRefreshShown && (
            <Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
          )}

          {isLiveStatusShown && (
            <Button size="small" variant="contained" color="error">
              Live
            </Button>
          )}
          {isDatePickerRequired && (
            <CustomDatePicker ref={customDatePickerRef} onChange={(date, type) => handleDateChange(date, type)} />
          )}

          {isRefreshShown && filterType !== RANGE_TYPE_LABELS.CUSTOM && (
            <IconButton onClick={handleRefreshClick}>
              <RefreshCcw size={18} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* -------- Advanced Filters -------- */}
      {isAdvancedFiltersShown && showAdvancedFilters && (
        <Box mt={2}>
          <AdvancedFilters
            messageType={filters?.msgType || ""}
            feature={filters?.feature || []}
            details={filters?.search || ""}
            onMessageTypeChange={onMessageTypeChange}
            onFeatureChange={onFeatureChange}
            onDetailSearch={onDetailSearch}
          />
        </Box>
      )}

      {/* -------- Chips -------- */}
      {activeFilterChips.length > 0 && (
        <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
          {activeFilterChips.map(chip => (
            <Chip
              key={chip.id}
              label={chip.label}
              onDelete={() => handleRemoveChip(chip)}
              deleteIcon={<X size={12} />}
              sx={{
                height: 22,
                fontSize: "11px",
                px: 0.5,
                backgroundColor: "#f1f5f9",
              }}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default FilterBar;

FilterBar.propTypes = {
  isRefreshShown: PropTypes.bool,
  isAutoRefreshShown: PropTypes.bool,
  isLiveStatusShown: PropTypes.bool,
  onSearch: PropTypes.func,
  onDateChange: PropTypes.func,
  onMessageTypeChange: PropTypes.func,
  onFeatureChange: PropTypes.func,
  onDetailSearch: PropTypes.func,
  filters: PropTypes.object,
  isAdvancedFiltersShown: PropTypes.bool,
  handleRefresh: PropTypes.func,
  isDatePickerRequired: PropTypes.bool,
  isSerialNumberRequired: PropTypes.bool,
};
